import {
  type CarrierContactFragment,
  type CarrierContactType,
  useRemoveCarrierContactFromAppetiteProductMutation,
} from "@qw/graphql";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenuItem,
  Group,
  Icon,
  MoreMenu,
  useToast,
} from "@qw/ui";
import { useState } from "react";

import { CarrierContactForm } from "./carrier-contact-form";

interface Props {
  contact: CarrierContactFragment;
  type: CarrierContactType;
  appetiteProductId: string;
  canEdit: boolean;
}

export const CarrierContactCard: React.FC<Props> = ({ contact, type, appetiteProductId, canEdit }) => {
  const { toast } = useToast();
  const [removeCarrierContact] = useRemoveCarrierContactFromAppetiteProductMutation();

  const [open, setOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { id, firstName, lastName, email, phone, title } = contact;

  const handleDelete = () =>
    removeCarrierContact({
      variables: { input: { id, appetiteProductId, type } },
      onCompleted: () => toast({ title: "Contact removed" }),
      refetchQueries: ["Product"],
    });

  return (
    <Group className="border-b last:border-b-0 p-2">
      <Group className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium">
            {firstName} {lastName}
          </p>
          <a href={`mailto:${email}`} className="text-sm text-muted-foreground hover:text-foreground">
            {email}
          </a>
          {title && <p className="text-sm text-muted-foreground">{title}</p>}
          {phone && <p className="text-sm text-muted-foreground">{phone}</p>}
        </div>

        {canEdit && (
          <>
            <MoreMenu icon="more_vert">
              <DropdownMenuItem onClick={() => setOpen(true)}>
                <Icon icon="edit" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={() => setConfirmDeleteOpen(true)}>
                <Icon icon="delete" />
                Remove
              </DropdownMenuItem>
            </MoreMenu>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Contact</DialogTitle>
                </DialogHeader>
                <CarrierContactForm
                  carrierContact={contact}
                  type={type}
                  appetiteProductId={appetiteProductId}
                  onCompleted={() => setOpen(false)}
                />
              </DialogContent>
            </Dialog>

            <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Remove Contact</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to remove {firstName} {lastName} from this product?
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      handleDelete();
                      setConfirmDeleteOpen(false);
                    }}
                  >
                    Remove
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </Group>
    </Group>
  );
};
